/**
 * Reusable checklist card for AI assistant option questions.
 * Multi-select supported; submit once (not on every toggle).
 */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useId, useState } from 'react';

import type { ChatChecklistPart, ChatChecklistSubmission } from './chatMessageParts';

// ═══════════════════════════════════════════════════════════════════════
//  Pure selection/validation logic — extracted so it is unit-testable
//  without a DOM (this repo's Vitest environment does not configure jsdom).
// ═══════════════════════════════════════════════════════════════════════

export interface ChecklistToggleResult {
  selectedIds: string[];
  error?: string;
}

/** Applies a single option toggle, respecting selectionMode/maxSelections. */
export function toggleChecklistOption(
  selectedIds: string[],
  optionId: string,
  checklist: Pick<ChatChecklistPart, 'selectionMode' | 'maxSelections'>,
): ChecklistToggleResult {
  const maxSelections = checklist.maxSelections ?? (checklist.selectionMode === 'single' ? 1 : undefined);
  if (checklist.selectionMode === 'single' || maxSelections === 1) {
    return { selectedIds: selectedIds.includes(optionId) ? [] : [optionId] };
  }
  if (selectedIds.includes(optionId)) {
    return { selectedIds: selectedIds.filter((id) => id !== optionId) };
  }
  if (maxSelections !== undefined && selectedIds.length >= maxSelections) {
    return {
      selectedIds,
      error: `Select at most ${maxSelections} option${maxSelections === 1 ? '' : 's'}.`,
    };
  }
  return { selectedIds: [...selectedIds, optionId] };
}

export interface ChecklistSubmissionInput {
  selectedIds: string[];
  otherChecked: boolean;
  otherText: string;
}

export type ChecklistSubmissionResult =
  | { ok: true; submission: ChatChecklistSubmission }
  | { ok: false; error: string };

/** Validates the current selection and builds the submission payload, or returns a validation error. */
export function buildChecklistSubmission(
  checklist: ChatChecklistPart,
  input: ChecklistSubmissionInput,
): ChecklistSubmissionResult {
  const minSelections = checklist.minSelections ?? (checklist.selectionMode === 'single' ? 1 : 0);
  const effectiveCount = input.selectedIds.length + (input.otherChecked && input.otherText.trim() ? 1 : 0);
  if (effectiveCount < minSelections && !(checklist.allowSkip && effectiveCount === 0)) {
    return {
      ok: false,
      error:
        minSelections <= 1
          ? 'Select at least one option to continue.'
          : `Select at least ${minSelections} options to continue.`,
    };
  }
  if (input.otherChecked && !input.otherText.trim()) {
    return { ok: false, error: 'Enter a value for “Something else”, or uncheck it.' };
  }
  const selectedLabels = checklist.options
    .filter((opt) => input.selectedIds.includes(opt.id))
    .map((opt) => opt.label);
  if (input.otherChecked && input.otherText.trim()) {
    selectedLabels.push(input.otherText.trim());
  }
  return {
    ok: true,
    submission: {
      checklistId: checklist.id,
      selectedOptionIds: input.selectedIds,
      selectedLabels,
      otherText: input.otherChecked ? input.otherText.trim() || undefined : undefined,
    },
  };
}

/** Builds the submission payload for a skipped checklist question. */
export function buildChecklistSkipSubmission(checklist: Pick<ChatChecklistPart, 'id'>): ChatChecklistSubmission {
  return { checklistId: checklist.id, selectedOptionIds: [], selectedLabels: [], skipped: true };
}

export interface ChatChecklistCardProps {
  checklist: ChatChecklistPart;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  onSubmit: (submission: ChatChecklistSubmission) => void;
  onSkip?: () => void;
  onRetry?: () => void;
}

export function ChatChecklistCard({
  checklist,
  disabled = false,
  loading = false,
  error = null,
  onSubmit,
  onSkip,
  onRetry,
}: ChatChecklistCardProps): JSX.Element {
  const legendId = useId();
  const errorId = useId();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const [otherChecked, setOtherChecked] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const selectionMode = checklist.selectionMode;
  const maxSelections =
    checklist.maxSelections
    ?? (selectionMode === 'single' ? 1 : undefined);

  const toggleOption = (optionId: string): void => {
    if (disabled || loading || submitted) return;
    setValidationError(null);
    const result = toggleChecklistOption(selectedIds, optionId, checklist);
    setSelectedIds(result.selectedIds);
    if (result.error) setValidationError(result.error);
  };

  const handleSubmit = (): void => {
    if (disabled || loading || submitted) return;
    const result = buildChecklistSubmission(checklist, { selectedIds, otherChecked, otherText });
    if (!result.ok) {
      setValidationError(result.error);
      return;
    }
    setSubmitted(true);
    onSubmit(result.submission);
  };

  const handleSkip = (): void => {
    if (disabled || loading || submitted || !checklist.allowSkip) return;
    setSubmitted(true);
    onSkip?.();
    onSubmit(buildChecklistSkipSubmission(checklist));
  };

  const displayError = validationError || error;

  return (
    <Box
      component="fieldset"
      aria-labelledby={legendId}
      aria-describedby={displayError ? errorId : undefined}
      p="md"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 8,
        background: 'var(--mantine-color-gray-0)',
        width: '100%',
        margin: 0,
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Text id={legendId} component="legend" size="sm" fw={600} style={{ padding: 0 }}>
            {checklist.prompt}
          </Text>
          {checklist.progress && (
            <Text size="xs" c="dimmed" aria-label={`Step ${checklist.progress.current} of ${checklist.progress.total}`}>
              {checklist.progress.current}/{checklist.progress.total}
            </Text>
          )}
        </Group>

        <Stack gap="xs" role="group">
          {checklist.options.map((option) => {
            const checked = selectedIds.includes(option.id);
            const Control = selectionMode === 'single' || maxSelections === 1 ? Radio : Checkbox;
            return (
              <Box
                key={option.id}
                p="sm"
                style={{
                  borderRadius: 6,
                  border: checked
                    ? '1px solid var(--mantine-color-gold-5)'
                    : '1px solid var(--mantine-color-gray-3)',
                  background: checked ? 'var(--mantine-color-gold-0)' : 'white',
                  width: '100%',
                }}
              >
                <Control
                  label={
                    <Stack gap={2}>
                      <Text size="sm">{option.label}</Text>
                      {option.description && (
                        <Text size="xs" c="dimmed">{option.description}</Text>
                      )}
                    </Stack>
                  }
                  checked={checked}
                  disabled={disabled || loading || submitted || option.disabled || (selectionMode === 'single' ? false : undefined)}
                  onChange={() => toggleOption(option.id)}
                  value={option.id}
                />
              </Box>
            );
          })}

          {checklist.allowOther && (
            <Box
              p="sm"
              style={{
                borderRadius: 6,
                border: otherChecked
                  ? '1px solid var(--mantine-color-gold-5)'
                  : '1px solid var(--mantine-color-gray-3)',
                background: otherChecked ? 'var(--mantine-color-gold-0)' : 'white',
                width: '100%',
              }}
            >
              <Checkbox
                label={checklist.otherLabel ?? 'Something else'}
                checked={otherChecked}
                disabled={disabled || loading || submitted}
                onChange={(event) => {
                  setOtherChecked(event.currentTarget.checked);
                  setValidationError(null);
                }}
              />
              {otherChecked && (
                <TextInput
                  mt="xs"
                  aria-label={checklist.otherLabel ?? 'Something else'}
                  value={otherText}
                  onChange={(event) => {
                    setOtherText(event.currentTarget.value);
                    setValidationError(null);
                  }}
                  disabled={disabled || loading || submitted}
                  placeholder="Describe…"
                />
              )}
            </Box>
          )}
        </Stack>

        {displayError && (
          <Alert id={errorId} color="red" variant="light" role="alert">
            {displayError}
            {error && onRetry && (
              <Button mt="xs" size="compact-xs" variant="light" onClick={onRetry}>
                Retry
              </Button>
            )}
          </Alert>
        )}

        <Group justify="flex-end" gap="xs" wrap="wrap">
          {checklist.allowSkip && (
            <Button
              variant="subtle"
              color="gray"
              disabled={disabled || loading || submitted}
              onClick={handleSkip}
            >
              Skip
            </Button>
          )}
          <Button
            color="gold"
            loading={loading}
            disabled={disabled || submitted}
            onClick={handleSubmit}
          >
            {checklist.submitLabel ?? 'Continue'}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
